export {};

type QoraRole = "assistant" | "user";

type QoraMessage = {
  role: QoraRole;
  content: string;
};

type ChatCompletionStreamEvent = {
  choices?: Array<{
    delta?: {
      content?: string;
    };
    finish_reason?: string | null;
  }>;
};

type StreamHandlers = {
  onContent: (content: string) => void;
  onDone: () => void;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance;

interface SpeechRecognitionAlternativeLike {
  transcript: string;
}

interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternativeLike | undefined;
}

interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike | undefined;
}

interface SpeechRecognitionEventLike extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionErrorEventLike extends Event {
  readonly error?: string;
  readonly message?: string;
}

interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  abort: () => void;
  start: () => void;
  stop: () => void;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

const PRODUCTION_CHAT_ENDPOINT = "https://fabric.qorbit.com/chat/api";

function chatEndpoint(hostname = globalThis.location?.hostname ?? ""): string {
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `http://${hostname}:3010/chat/api`;
  }
  return PRODUCTION_CHAT_ENDPOINT;
}
const SESSION_KEY = "qora-session";
const EMBED_SESSION_PREFIX = "qora-chat-session:";
const ROOT_ID = "qora-chat-widget-root";
const GREETING = "Hi, I'm Qora. How can I help you learn about Qorbit?";
const STREAM_STALL_MS = 1_500;

const styles = `
  :host {
    all: initial;
    color-scheme: dark;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  .qora-shell {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 99999;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }

  .qora-launcher {
    width: 56px;
    height: 56px;
    border: 0;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: #00C896;
    color: #04120e;
    cursor: pointer;
    box-shadow: 0 16px 40px rgba(0, 200, 150, 0.28), 0 10px 24px rgba(0, 0, 0, 0.35);
    transition: transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease;
  }

  .qora-launcher:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 48px rgba(0, 200, 150, 0.34), 0 12px 28px rgba(0, 0, 0, 0.4);
  }

  .qora-launcher:focus-visible,
  .qora-close:focus-visible,
  .qora-send:focus-visible,
  .qora-mic:focus-visible,
  .qora-input:focus-visible {
    outline: 2px solid #00C896;
    outline-offset: 3px;
  }

  .qora-icon {
    width: 26px;
    height: 26px;
    display: block;
  }

  .qora-panel {
    position: absolute;
    right: 0;
    bottom: 72px;
    width: min(380px, calc(100vw - 48px));
    height: min(620px, calc(100vh - 120px));
    min-height: 420px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    background: #0a0f1a;
    box-shadow: 0 28px 80px rgba(0, 0, 0, 0.52), 0 0 0 1px rgba(0, 200, 150, 0.06);
    transform: translate3d(20px, 24px, 0) scale(0.96);
    transform-origin: bottom right;
    opacity: 0;
    pointer-events: none;
    transition: opacity 180ms ease, transform 180ms ease;
  }

  .qora-panel[data-open="true"] {
    transform: translate3d(0, 0, 0) scale(1);
    opacity: 1;
    pointer-events: auto;
  }

  .qora-shell[data-open="true"] .qora-launcher {
    opacity: 0;
    pointer-events: none;
    transform: translateY(8px) scale(0.94);
  }

  .qora-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.025);
  }

  .qora-title {
    margin: 0;
    color: #ffffff;
    font-size: 16px;
    font-weight: 650;
    line-height: 1.2;
  }

  .qora-subtitle {
    margin: 3px 0 0;
    color: #a0a0b0;
    font-size: 12px;
    line-height: 1.3;
  }

  .qora-close {
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: rgba(255, 255, 255, 0.06);
    color: #ffffff;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
  }

  .qora-close:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .qora-messages {
    flex: 1;
    overflow-y: auto;
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    scrollbar-width: thin;
    scrollbar-color: rgba(160, 160, 176, 0.35) transparent;
  }

  .qora-message-row {
    display: flex;
    width: 100%;
  }

  .qora-message-row[data-role="user"] {
    justify-content: flex-end;
  }

  .qora-message-row[data-role="assistant"] {
    justify-content: flex-start;
  }

  .qora-bubble {
    max-width: 82%;
    border-radius: 12px;
    padding: 12px;
    color: #ffffff;
    font-size: 14px;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .qora-message-row[data-role="user"] .qora-bubble {
    background: #111827;
    border-bottom-right-radius: 4px;
  }

  .qora-message-row[data-role="assistant"] .qora-bubble {
    background: #182033;
    border-bottom-left-radius: 4px;
  }

  .qora-loading {
    display: inline-flex;
    align-items: center;
    color: #a0a0b0;
    font-size: 12px;
    line-height: 1.3;
    animation: qora-status-pulse 1.6s ease-in-out infinite;
  }

  .qora-status {
    display: inline-flex;
    align-items: center;
    max-width: 82%;
    padding: 0 4px;
    color: #a0a0b0;
    font-size: 12px;
    line-height: 1.3;
    animation: qora-status-pulse 1.6s ease-in-out infinite;
  }

  @keyframes qora-status-pulse {
    0%, 100% {
      opacity: 0.52;
    }
    50% {
      opacity: 1;
    }
  }

  .qora-footer {
    display: grid;
    gap: 8px;
    padding: 10px 14px 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.025);
  }

  .qora-form {
    display: flex;
    align-items: flex-end;
    gap: 10px;
  }

  .qora-input-wrap {
    position: relative;
    min-width: 0;
    flex: 1;
  }

  .qora-input {
    width: 100%;
    max-height: 104px;
    resize: none;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 14px;
    padding: 11px 46px 11px 13px;
    background: #111827;
    color: #ffffff;
    font: inherit;
    font-size: 14px;
    line-height: 1.35;
  }

  .qora-input-wrap[data-voice="false"] .qora-input {
    padding-right: 13px;
  }

  .qora-input::placeholder {
    color: #a0a0b0;
  }

  .qora-send {
    width: 42px;
    height: 42px;
    flex: 0 0 auto;
    border: 0;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: #00C896;
    color: #04120e;
    cursor: pointer;
    transition: opacity 160ms ease, transform 160ms ease;
  }

  .qora-mic {
    position: absolute;
    right: 6px;
    bottom: 6px;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: rgba(255, 255, 255, 0.06);
    color: #a0a0b0;
    cursor: pointer;
    transition: background 160ms ease, color 160ms ease, opacity 160ms ease;
  }

  .qora-mic:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }

  .qora-mic[data-listening="true"] {
    background: rgba(0, 200, 150, 0.18);
    color: #00C896;
    animation: qora-status-pulse 1.2s ease-in-out infinite;
  }

  .qora-send:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  .qora-send:disabled,
  .qora-input:disabled,
  .qora-mic:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .qora-error {
    color: #fca5a5;
  }

  .qora-voice-note {
    color: #fca5a5;
    font-size: 12px;
    line-height: 1.3;
  }

  .qora-voice-note:empty {
    display: none;
  }

  @media (max-width: 639px) {
    .qora-shell {
      right: 16px;
      bottom: 16px;
      left: 16px;
    }

    .qora-launcher {
      margin-left: auto;
    }

    .qora-panel {
      position: fixed;
      left: 16px;
      right: 16px;
      bottom: 16px;
      width: calc(100vw - 32px);
      height: 85vh;
      min-height: 0;
      transform-origin: bottom center;
    }
  }
`;

function createUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function readEmbedKey(): string {
  const tagged = document.querySelectorAll("script[data-embed-key]");
  for (const node of tagged) {
    const value = node.getAttribute("data-embed-key")?.trim();
    if (value) return value;
  }
  const scripts = document.querySelectorAll('script[src*="qorbit-chat.js"]');
  for (const node of scripts) {
    const value = node.getAttribute("data-embed-key")?.trim();
    if (value) return value;
  }
  return "";
}

function sessionStorageKey(embedKey: string): string {
  return embedKey ? `${EMBED_SESSION_PREFIX}${embedKey}` : SESSION_KEY;
}

function getOrCreateSessionId(embedKey = ""): string {
  const key = sessionStorageKey(embedKey);
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const next = createUuid();
    window.sessionStorage.setItem(key, next);
    return next;
  } catch {
    return createUuid();
  }
}

function rememberServerSession(embedKey: string, chatSession: string): void {
  if (!embedKey || !chatSession) return;
  try {
    window.sessionStorage.setItem(sessionStorageKey(embedKey), chatSession);
  } catch {
    // sessionStorage may be unavailable; the next turn will mint again.
  }
}

function createElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  return element;
}

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

async function requestQora(
  messages: QoraMessage[],
  sessionId: string,
  handlers: StreamHandlers,
  embedKey: string,
): Promise<string> {
  const payload: Record<string, unknown> = {
    user: `web:${sessionId}`,
    messages,
    stream: true,
  };
  if (embedKey) {
    payload.embedKey = embedKey;
    payload.chatSession = sessionId;
  }
  const response = await fetch(chatEndpoint(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Qora request failed with status ${response.status}`);
  }

  const issuedSession = response.headers.get("x-qorbit-chat-session");
  if (issuedSession) rememberServerSession(embedKey, issuedSession);

  if (!response.body) {
    throw new Error("Qora response did not include a readable stream");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullContent = "";
  let streamFinished = false;
  let completionSeen = false;

  const markComplete = (): void => {
    if (completionSeen) return;
    completionSeen = true;
    handlers.onDone();
  };

  const processLine = (rawLine: string): void => {
    const line = rawLine.trimEnd();
    if (!line.startsWith("data: ")) return;

    const data = line.slice(6).trim();
    if (data === "[DONE]") {
      streamFinished = true;
      markComplete();
      return;
    }

    try {
      const parsed = JSON.parse(data) as ChatCompletionStreamEvent;
      const token = parsed.choices?.[0]?.delta?.content;
      if (token) {
        fullContent += token;
        handlers.onContent(fullContent);
      }

      if (parsed.choices?.some((choice) => choice.finish_reason === "stop")) {
        markComplete();
      }
    } catch {
      // Ignore malformed SSE keepalive or partial lines; buffering handles chunk boundaries.
    }
  };

  while (!streamFinished) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      processLine(line);
      if (streamFinished) break;
    }
  }

  const remaining = `${buffer}${decoder.decode()}`;
  if (remaining.trim()) {
    processLine(remaining);
  }

  if (!completionSeen) {
    throw new Error("Qora stream closed before completion");
  }

  if (!fullContent.trim()) {
    throw new Error("Qora stream did not include message content");
  }

  return fullContent;
}

function initQoraChat(): void {
  if (document.getElementById(ROOT_ID)) return;

  const embedKey = readEmbedKey();
  let sessionId: string | null = getOrCreateSessionId(embedKey);
  let isOpen = false;
  let isLoading = false;
  const messages: QoraMessage[] = [{ role: "assistant", content: GREETING }];

  const host = document.createElement("div");
  host.id = ROOT_ID;
  const shadowRoot = host.attachShadow({ mode: "open" });

  const styleElement = document.createElement("style");
  styleElement.textContent = styles;
  shadowRoot.append(styleElement);

  const shell = createElement("div", "qora-shell");
  const launcher = createElement("button", "qora-launcher");
  launcher.type = "button";
  launcher.setAttribute("aria-label", "Open Qora chat");
  launcher.innerHTML = `
    <svg class="qora-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M4.5 5.75A3.75 3.75 0 0 1 8.25 2h7.5a3.75 3.75 0 0 1 3.75 3.75v5.5A3.75 3.75 0 0 1 15.75 15h-3.9l-4.68 4.02A1 1 0 0 1 5.5 18.26V15.1a3.75 3.75 0 0 1-1-2.55v-6.8Z"/>
    </svg>
  `;

  const panel = createElement("section", "qora-panel");
  panel.setAttribute("aria-label", "Qora chat");
  panel.dataset.open = "false";

  const header = createElement("div", "qora-header");
  const heading = createElement("div");
  const title = createElement("p", "qora-title");
  title.textContent = "Qora";
  const subtitle = createElement("p", "qora-subtitle");
  subtitle.textContent = "Qorbit product specialist";
  heading.append(title, subtitle);

  const closeButton = createElement("button", "qora-close");
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Close Qora chat");
  closeButton.textContent = "x";
  header.append(heading, closeButton);

  const messageList = createElement("div", "qora-messages");
  messageList.setAttribute("role", "log");
  messageList.setAttribute("aria-live", "polite");

  const form = createElement("form", "qora-form");
  const footer = createElement("div", "qora-footer");
  const voiceNote = createElement("div", "qora-voice-note");
  const input = createElement("textarea", "qora-input");
  input.rows = 1;
  input.placeholder = "Ask about Qorbit...";
  input.setAttribute("aria-label", "Message Qora");
  const inputWrap = createElement("div", "qora-input-wrap");
  inputWrap.append(input);

  const SpeechRecognitionCtor = getSpeechRecognitionConstructor();
  inputWrap.dataset.voice = SpeechRecognitionCtor ? "true" : "false";
  let micButton: HTMLButtonElement | null = null;
  let recognition: SpeechRecognitionInstance | null = null;
  let isListening = false;
  let finalTranscript = "";

  if (SpeechRecognitionCtor) {
    micButton = createElement("button", "qora-mic");
    micButton.type = "button";
    micButton.dataset.listening = "false";
    micButton.setAttribute("aria-label", "Start voice input");
    micButton.title = "Start voice input";
    micButton.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Zm5-3a1 1 0 1 1 2 0 7 7 0 0 1-6 6.93V20h3a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2h3v-2.07A7 7 0 0 1 5 11a1 1 0 1 1 2 0 5 5 0 0 0 10 0Z"/>
      </svg>
    `;
    inputWrap.append(micButton);
  }

  const sendButton = createElement("button", "qora-send");
  sendButton.type = "submit";
  sendButton.setAttribute("aria-label", "Send message");
  sendButton.innerHTML = `
    <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M3.58 20.4 21.2 12 3.58 3.6 3 10.2 14.7 12 3 13.8l.58 6.6Z"/>
    </svg>
  `;
  form.append(inputWrap, sendButton);
  footer.append(voiceNote, form);

  panel.append(header, messageList, footer);
  shell.append(launcher, panel);
  shadowRoot.append(shell);
  document.body.append(host);

  function scrollToBottom(): void {
    messageList.scrollTop = messageList.scrollHeight;
  }

  function appendMessage(message: QoraMessage, extraClass?: string): HTMLElement {
    const row = createElement("div", "qora-message-row");
    row.dataset.role = message.role;
    const bubble = createElement("div", `qora-bubble${extraClass ? ` ${extraClass}` : ""}`);
    bubble.textContent = message.content;
    row.append(bubble);
    messageList.append(row);
    scrollToBottom();
    return row;
  }

  function appendStreamingMessage(): { row: HTMLElement; bubble: HTMLElement } {
    const row = createElement("div", "qora-message-row");
    row.dataset.role = "assistant";
    const bubble = createElement("div", "qora-bubble");
    bubble.innerHTML = '<span class="qora-loading" aria-label="Qora is responding">Thinking...</span>';
    row.append(bubble);
    messageList.append(row);
    scrollToBottom();
    return { row, bubble };
  }

  function appendStatusMessage(label: string): HTMLElement {
    const row = createElement("div", "qora-message-row");
    row.dataset.role = "assistant";
    const status = createElement("div", "qora-status");
    status.setAttribute("aria-live", "polite");
    status.textContent = label;
    row.append(status);
    messageList.append(row);
    scrollToBottom();
    return row;
  }

  function renderInitialMessages(): void {
    messageList.textContent = "";
    for (const message of messages) {
      appendMessage(message);
    }
  }

  function setLoading(nextValue: boolean): void {
    isLoading = nextValue;
    input.disabled = nextValue;
    sendButton.disabled = nextValue;
    if (micButton) {
      micButton.disabled = nextValue;
    }
  }

  function resizeInput(): void {
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, 104)}px`;
  }

  function showVoiceNote(message: string): void {
    voiceNote.textContent = message;
    window.setTimeout(() => {
      if (voiceNote.textContent === message) {
        voiceNote.textContent = "";
      }
    }, 4_000);
  }

  function setListening(nextValue: boolean): void {
    isListening = nextValue;
    if (!micButton) return;
    micButton.dataset.listening = String(nextValue);
    micButton.setAttribute("aria-label", nextValue ? "Stop voice input" : "Start voice input");
    micButton.title = nextValue ? "Stop voice input" : "Start voice input";
  }

  function appendTranscript(text: string): void {
    const transcript = text.trim();
    if (!transcript) return;
    const prefix = input.value.trim() ? `${input.value.trimEnd()} ` : "";
    input.value = `${prefix}${transcript}`;
    resizeInput();
    input.focus();
  }

  function stopVoiceRecognition(): void {
    if (!recognition || !isListening) return;
    recognition.stop();
  }

  function startVoiceRecognition(): void {
    if (!SpeechRecognitionCtor || isLoading) return;
    voiceNote.textContent = "";
    finalTranscript = "";

    try {
      recognition?.abort();
    } catch {
      // Ignore stale browser recognizer cleanup failures.
    }

    const nextRecognition = new SpeechRecognitionCtor();
    recognition = nextRecognition;
    nextRecognition.continuous = false;
    nextRecognition.interimResults = true;
    nextRecognition.lang = navigator.language || "en-US";
    nextRecognition.onresult = (event) => {
      const parts: string[] = [];
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript;
        if (result?.isFinal && transcript) {
          parts.push(transcript.trim());
        }
      }
      const finalText = parts.filter(Boolean).join(" ");
      if (finalText) {
        finalTranscript = finalTranscript ? `${finalTranscript} ${finalText}` : finalText;
      }
    };
    nextRecognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        showVoiceNote("Microphone permission was denied. You can keep typing.");
      } else {
        showVoiceNote("Voice input stopped. You can keep typing.");
      }
      setListening(false);
    };
    nextRecognition.onend = () => {
      setListening(false);
      appendTranscript(finalTranscript);
      recognition = null;
    };

    try {
      nextRecognition.start();
      setListening(true);
    } catch {
      recognition = null;
      setListening(false);
      showVoiceNote("Voice input could not start. You can keep typing.");
    }
  }

  function openPanel(): void {
    sessionId = sessionId ?? getOrCreateSessionId();
    isOpen = true;
    shell.dataset.open = "true";
    panel.dataset.open = "true";
    input.focus();
    scrollToBottom();
  }

  function closePanel(): void {
    isOpen = false;
    shell.dataset.open = "false";
    panel.dataset.open = "false";
    launcher.focus();
  }

  async function sendMessage(rawContent: string): Promise<void> {
    const content = rawContent.trim();
    if (!content || isLoading) return;
    stopVoiceRecognition();

    const userMessage: QoraMessage = { role: "user", content };
    messages.push(userMessage);
    appendMessage(userMessage);
    input.value = "";
    input.style.height = "auto";
    setLoading(true);
    const streamingMessage = appendStreamingMessage();
    let stallRow: HTMLElement | null = null;
    let stallTimer: number | null = null;

    const clearStallTimer = (): void => {
      if (stallTimer === null) return;
      window.clearTimeout(stallTimer);
      stallTimer = null;
    };
    const hideStallStatus = (): void => {
      stallRow?.remove();
      stallRow = null;
    };
    const scheduleStallStatus = (): void => {
      clearStallTimer();
      stallTimer = window.setTimeout(() => {
        stallRow = stallRow ?? appendStatusMessage("Working on it...");
      }, STREAM_STALL_MS);
    };
    const finishStatus = (): void => {
      clearStallTimer();
      hideStallStatus();
    };

    try {
      let hasFirstToken = false;
      const responseContent = await requestQora(
        messages,
        sessionId ?? getOrCreateSessionId(embedKey),
        {
          onContent: (partialContent) => {
            if (!hasFirstToken) {
              hasFirstToken = true;
              streamingMessage.bubble.textContent = "";
            }
            hideStallStatus();
            streamingMessage.bubble.textContent = partialContent;
            scheduleStallStatus();
            scrollToBottom();
          },
          onDone: finishStatus,
        },
        embedKey,
      );
      sessionId = getOrCreateSessionId(embedKey);
      finishStatus();
      const assistantMessage: QoraMessage = { role: "assistant", content: responseContent };
      messages.push(assistantMessage);
    } catch (error) {
      console.error("[qora-chat] request failed", error);
      finishStatus();
      streamingMessage.row.remove();
      appendMessage(
        {
          role: "assistant",
          content: "I couldn't reach Qora just now. Please try again in a moment.",
        },
        "qora-error",
      );
    } finally {
      finishStatus();
      setLoading(false);
      input.focus();
    }
  }

  launcher.addEventListener("click", openPanel);
  closeButton.addEventListener("click", closePanel);
  micButton?.addEventListener("click", () => {
    if (isListening) {
      stopVoiceRecognition();
      return;
    }
    startVoiceRecognition();
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void sendMessage(input.value);
  });

  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void sendMessage(input.value);
    }
  });

  input.addEventListener("input", () => {
    resizeInput();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && isOpen) {
      closePanel();
    }
  });

  renderInitialMessages();
  if (embedKey && sessionId) {
    void fetch(chatEndpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "history",
        embedKey,
        chatSession: sessionId,
      }),
    })
      .then(async (response) => {
        if (!response.ok) return;
        const issued = response.headers.get("x-qorbit-chat-session");
        if (issued) {
          rememberServerSession(embedKey, issued);
          sessionId = issued;
        }
        const payload = (await response.json()) as { messages?: QoraMessage[] };
        if (!Array.isArray(payload.messages) || payload.messages.length === 0) return;
        messages.splice(0, messages.length, ...payload.messages);
        renderInitialMessages();
      })
      .catch(() => {
        // History restore is best-effort; the widget still accepts a new turn.
      });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initQoraChat, { once: true });
} else {
  initQoraChat();
}
